import {
  InlineDataServiceResponseError,
  InlineDatumProps,
  useAnalytics,
  useInlineDataEvents,
} from '@stripe-internal/inline-data';
import {
  SendUsAnEmailFormValues,
  TextRow,
  useViewMode,
} from '@stripe-internal/conversations-ui';
import {useCallback, useEffect, useRef, useState} from 'react';
import {captureException, captureMessage} from '@sentry/browser';
import {AnimatePresence, animate} from '@sail/engine';
import {view} from '@sail/ui';
import {defineMessages, FormattedMessage} from 'react-intl';
import {SendUsAnEmailForm} from '../UniversalHelpChannelMenu/SendUsAnEmailForm';
import {useTransitionElements} from '../UniversalHelpChannelMenu/useTransitionElements';

const ANIMATION_DURATION = 350;

const messages = defineMessages({
  emailInstructionsMessage: {
    id: 'mse_inline_data.universal_help_channel_menu.email.emailInstructionsMessage',
    description:
      'Instructions shown to the user in the conversation log before they are shown the email form.',
    defaultMessage:
      'Sure. Please provide as much detail as possible and an agent will try to get back to you by email within 24 hours.',
  },
  emailExplanationMessage: {
    id: 'mse_inline_data.universal_help_channel_menu.email.emailExplanationMessage',
    description:
      'Instructions shown to the user in the conversation log after they successfully submit a email form support request',
    defaultMessage:
      'Email received. You can now close this drawer or ask a new question by typing below. This will clear your chat history.',
  },
  emailExplanationMessageWidget: {
    id: 'mse_inline_data.universal_help_channel_menu.email.emailExplanationMessageWidget',
    description:
      'Instructions shown to the user in the conversation log after they successfully submit a email form support request - for the widget',
    defaultMessage:
      'Email received. For further assistance, please close the window and start a new chat.',
  },
});

export type EmailClientErrors =
  | 'email-invalid-email'
  | 'email-user-has-no-email'
  | 'email-invalid-cc-emails'
  | 'email-invalid-message'
  | 'email-invalid-topic'
  | 'email-attachment-processing-error'
  | 'email-unauthorized'
  | 'email-invalid-uh-conversation-id'
  | 'email-rate-limited';

export type FollowUpEmailClientErrors =
  | 'follow-up-email-invalid-email'
  | 'follow-up-email-user-has-no-email'
  | 'follow-up-email-invalid-cc-emails'
  | 'follow-up-email-invalid-message'
  | 'follow-up-email-invalid-topic'
  | 'follow-up-email-attachment-processing-error'
  | 'follow-up-email-unauthorized'
  | 'follow-up-email-rate-limited'
  | 'follow-up-email-merchant-not-found'
  | 'follow-up-email-ticket-not-found'
  | 'follow-up-email-invalid-ticket';

export interface EmailForm {
  email: string;
  body: string;
  subject?: string;
  cc_emails: string[];
  attachment_ids: string[];
  issue_context: {
    icTagsEncountered: string[];
    articlesVisited: string[];
    transcript?: string;
  };
}

type Props = {
  action: InlineDatumProps['action'];
  nonce: string;
  handleCancelForm: () => void;
  emailForm?: EmailForm;
  isSectionVisible: boolean;
  isEmailFormSubmitted: boolean;
  showSubjectField?: boolean;
  showUnauthenticatedEmailField?: boolean;
  initialUnauthenticatedEmail?: string;
  showCcEmailsField?: boolean;
  existingCcEmails?: string[];
  clientError?: EmailClientErrors | FollowUpEmailClientErrors;
  serverError?: InlineDataServiceResponseError;
  showContactSupportOptions?: boolean;
  optionsType?: string;
};

type UHIssueContext = {
  icTagsEncountered: string[];
  articlesVisited: string[];
  transcript?: string;
};

function SendUsAnEmailSection({
  action,
  nonce,
  handleCancelForm,
  emailForm,
  isSectionVisible,
  isEmailFormSubmitted,
  clientError,
  serverError,
  showContactSupportOptions = false,
  optionsType = '',
  ...sendUsAnEmailFormProps
}: Props) {
  const mostRecentClientError = useRef<
    EmailClientErrors | FollowUpEmailClientErrors | undefined
  >();
  const hasLoggedClientError = useRef(false);

  const mostRecentServerError = useRef<
    InlineDataServiceResponseError | undefined
  >();
  const hasLoggedServerError = useRef(false);

  const [submitting, setSubmitting] = useState<boolean>(false);
  // Use state to track the first time we see the form is submitted to avoid animation thrash
  const [isFormSubmitted, setIsFormSubmitted] =
    useState<boolean>(isEmailFormSubmitted);

  const [initialMessage, setInitialMessage] = useState<string>('');
  const [issueContext, setIssueContext] = useState<UHIssueContext>({
    icTagsEncountered: [],
    articlesVisited: [],
    transcript: '',
  });

  const analytics = useAnalytics();
  const events = useInlineDataEvents();
  const {
    showInstructions,
    showForm,
    showSummary,
    showExplanation,
    setShowInstructions,
    setShowForm,
  } = useTransitionElements({
    isSectionVisible,
    isFormSubmitted,
  });

  const viewMode = useViewMode();

  // The inline data polls for updates and the error is only sent on submit; not poll.
  // Keep track of the most recent error and only clear on submit instead of clearing on each poll.
  useEffect(() => {
    if (clientError && !hasLoggedClientError.current) {
      analytics.track('support_conversations_email_client_error', {
        universalHelp: true,
      });
      captureMessage('Email submission error', {
        level: 'warning',
        extra: {
          responseType: clientError,
        },
      });
      mostRecentClientError.current = clientError;
      hasLoggedClientError.current = true;
    }

    if (clientError) {
      setSubmitting(false);
    }
  }, [clientError, analytics]);

  useEffect(() => {
    if (serverError && !hasLoggedServerError.current) {
      analytics.track('support_conversations_email_server_error', {
        universalHelp: true,
      });
      captureException(new Error(serverError.message));
      mostRecentServerError.current = serverError;
      hasLoggedServerError.current = true;
    }

    if (serverError) {
      setSubmitting(false);
    }
  }, [serverError, analytics]);

  useEffect(() => {
    if (isEmailFormSubmitted) {
      return;
    }

    const retrieveIssueContext = async () => {
      const result = await events.emitWithResult({
        name: 'inline-data-channel-form-issue-context',
        data: {},
        nonce,
      });

      if (!result.error && result.data?.issueContext) {
        setIssueContext(result.data.issueContext as UHIssueContext);
      }
    };

    retrieveIssueContext();
  }, [events, nonce, isEmailFormSubmitted]);

  useEffect(() => {
    if (isEmailFormSubmitted) {
      return;
    }

    const retrieveInitialMessage = async () => {
      const result = await events.emitWithResult({
        name: 'inline-data-channel-form-initial-message',
        data: {},
        nonce,
      });

      if (!result.error && typeof result.data?.initialMessage === 'string') {
        setInitialMessage(result.data?.initialMessage as string);
      }
    };

    retrieveInitialMessage();
  }, [events, isEmailFormSubmitted, nonce]);

  useEffect(() => {
    if (isEmailFormSubmitted) {
      setSubmitting(false);
      setIsFormSubmitted(true);
    }
  }, [isEmailFormSubmitted]);

  const handleSubmit = useCallback(
    async ({
      subject,
      ccEmails,
      unauthenticatedEmail,
      question,
      attachments,
    }: SendUsAnEmailFormValues) => {
      analytics.action('support_conversations_email_submit');
      mostRecentClientError.current = undefined;
      hasLoggedClientError.current = false;

      setSubmitting(true);

      action({
        selected_option_id: 'channel_email',
        show_contact_support_options: showContactSupportOptions,
        options_type: optionsType,
        email_form: {
          email: unauthenticatedEmail || '',
          body: question || '',
          subject,
          cc_emails: ccEmails || [],
          attachment_ids: attachments?.map((attachment) => attachment.id) || [],
          issue_context: issueContext,
        },
      });
    },
    [action, analytics, issueContext, showContactSupportOptions, optionsType],
  );

  const handleCancel = useCallback(() => {
    setShowInstructions(false);
    setShowForm(false);
    handleCancelForm();
  }, [handleCancelForm, setShowForm, setShowInstructions]);

  return (
    <view.div
      css={{
        stack: 'y',
        gap: 'large',
      }}
    >
      <AnimatePresence>
        {showInstructions ? (
          <view.div
            uses={[
              animate.in({
                duration: ANIMATION_DURATION,
                opacity: 0,
                easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
              }),
            ]}
          >
            {/* eslint-disable-next-line react/style-prop-object */}
            <TextRow body="" style="info">
              <FormattedMessage {...messages.emailInstructionsMessage} />
            </TextRow>
          </view.div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {showForm ? (
          <view.div
            uses={[
              animate.in({
                duration: ANIMATION_DURATION,
                opacity: 0,
                easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
              }),
            ]}
          >
            <SendUsAnEmailForm
              useNewDesigns
              handleSubmit={handleSubmit}
              onCancel={handleCancel}
              onEmailConfirmationClose={handleCancelForm}
              initialQuestionValue={initialMessage}
              submissionSuccess={isEmailFormSubmitted}
              isSubmissionPending={submitting}
              isSubmissionError={!!clientError}
              isRateLimitedError={
                clientError === 'email-rate-limited' ||
                clientError === 'follow-up-email-rate-limited'
              }
              hasCcEmailsError={
                clientError === 'email-invalid-cc-emails' ||
                clientError === 'follow-up-email-invalid-cc-emails'
              }
              hasUnauthenticatedEmailError={
                clientError === 'email-invalid-email' ||
                clientError === 'follow-up-email-invalid-email'
              }
              {...sendUsAnEmailFormProps}
            />
          </view.div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {showSummary && emailForm ? (
          <view.div
            uses={[
              animate.in({
                duration: ANIMATION_DURATION,
                opacity: 0,
                easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
              }),
            ]}
          >
            <view.div>
              <view.div
                className="uh-conversations-button-options-message-row"
                data-message-type="user"
                css={{
                  margin: 'small',
                }}
              >
                <TextRow position="end" body={emailForm.body || ''} />
              </view.div>
            </view.div>
          </view.div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {showExplanation ? (
          <view.div
            uses={[
              animate.in({
                duration: ANIMATION_DURATION,
                opacity: 0,
                easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
              }),
            ]}
          >
            {/* eslint-disable-next-line react/style-prop-object */}
            <TextRow body="" style="info">
              {viewMode === 'drawer' ? (
                <FormattedMessage {...messages.emailExplanationMessage} />
              ) : (
                <FormattedMessage {...messages.emailExplanationMessageWidget} />
              )}
            </TextRow>
          </view.div>
        ) : null}
      </AnimatePresence>
    </view.div>
  );
}

export default SendUsAnEmailSection;
